import {
    mergeTranslations,
    TranslationMessages as BaseTranslationMessages,
} from 'react-admin';
import englishMessages from 'ra-language-english';
import { RaTreeTranslationMessages } from '@react-admin/ra-tree';

export interface TranslationMessages
    extends RaTreeTranslationMessages, BaseTranslationMessages {}

const customEnglishMessages: TranslationMessages = mergeTranslations(
    englishMessages,
    {
        'ra-form-layout': {
            action: {
                next: 'Next',
            },
        },
        'ra-tree': {
            action: {
                add_root: 'Add a category of products',
            },
        },
        'ra-search': {
            result: `1 result |||| %{smart_count} results`,
        },
        'ra-realtime': {
            notification: {
                lock: {
                    lockedByMe: 'Locked for you',
                    lockedBySomeoneElse: 'Locked by someone else',
                    unlocked: 'Lock released',
                },
            },
        },
        'ra-preferences': {
            action: {
                choose_columns: 'Choose columns',
                toggle_theme: 'Toggle theme',
            },
        },
        ra: {
            action: {
                select_columns: 'Choose columns',
                toggle_theme: 'Toggle theme',
                copy: 'Copy to clipboard',
            },
            page: {
                empty: 'No results found',
            },
            saved_queries: {
                label: 'Saved',
                query_name: 'Query name',
                new_label: 'Save current query',
                new_dialog_title: 'Save current query as',
                remove_label: 'Remove saved query',
                remove_label_with_name: 'Remove query "%{name}"',
                remove_dialog_title: 'Remove saved query?',
                remove_message: 'Are you sure you want to remove the query?',
                help: 'First filter the list and save the query for future use.',
            },
        },
        actions: {
            export: 'Export to Excel',
        },
        notifications: {
            MPASS_REMOVED: 'MPASS identifier removed',
            ACTION_ERROR: 'Action failed',
            SESSION_REMOVED: 'Session removed and user logged out.',
            KV_USER_NOT_FOUND:
                'User not found in Kirjavälitys with this identifier',
            KV_EMAIL_ALREADY_USED: 'Email is already in use in Storia',
            NETWORK_ERRROR: 'Network error',
            EMAIL_ALREADY_USED: 'Email is already in use',
            EXPORT_ERROR: 'Error saving file',
        },
        menu: {
            audience: 'Users',
            sales: 'Test',
            catalog: 'Catalog',
            customers: 'Customers',
            hallinta: 'Administration',
        },
        hallinta: {
            tabs: {
                aws_overview: 'AWS Overview',
                secrets: 'Secrets Manager',
                jwt_keys: 'JWT Keys',
                oauth_clients: 'OAuth Clients',
                api_configs: 'API Configs',
            },
            api_configs: {
                fields: {
                    application_name: 'Application',
                    key: 'API Key',
                    name: 'Name',
                    type: 'Type',
                    is_default: 'Default',
                    role: 'Role',
                    policy: 'Policy',
                    adminrights: 'Admin Rights',
                    allowed_swaggers: 'Allowed Swaggers',
                    userrights: 'User Rights (JSON)',
                    jwt_algorithm: 'Algorithm',
                    jwt_ttl: 'Token TTL',
                    oidc_code_ttl: 'OIDC Code TTL',
                    session_config: 'Session Config (JSON)',
                    login_config: 'Login Config (JSON)',
                    scope: 'Scope',
                    status: 'Status',
                },
                sections: {
                    basic: 'Basic Info',
                    jwt: 'JWT Settings',
                    json_config: 'Settings',
                    jwt_keys: 'Linked JWT Signing Keys',
                    oauth_clients: 'Linked OAuth Clients',
                },
                actions: {
                    back: 'API Configs',
                },
                no_jwt_keys: 'No JWT signing keys linked to this config.',
                no_oauth_clients: 'No OAuth clients linked to this config.',
            },
            oauth_clients: {
                fields: {
                    name: 'Name',
                    type: 'Type',
                    client_id: 'Client ID',
                    client_secret: 'Client Secret',
                    api_key: 'API Key',
                    env: 'Environment',
                    status: 'Status',
                    theme: 'Theme',
                    back_channel_logout_uri: 'Back-channel Logout URI',
                    applications: 'Applications',
                    created: 'Created',
                    modified: 'Modified',
                    guid: 'GUID',
                    basic_auth_badge: 'Basic Auth',
                },
                status: {
                    active: 'Active',
                    disabled: 'Disabled',
                },
                tabs: {
                    basic_info: 'Basic Info',
                    applications: 'Applications',
                },
                sections: {
                    info_config: 'Settings',
                    info_config_openid: 'OpenID Settings',
                    basic_auth: 'Basic Auth Settings',
                },
                actions: {
                    back: 'OAuth Clients',
                    delete: 'Delete',
                    add_application: 'Add Application',
                },
                bulk_delete: {
                    button: 'Delete (%{count})',
                    confirm_title: 'Confirm permanent deletion',
                    confirm_permanent:
                        '%{count} selected client(s) have status "Disabled" and will be permanently deleted along with all their application links. This action cannot be undone.',
                    confirm_soft:
                        '%{count} active client(s) will be soft-deleted (disabled).',
                    cancel: 'Cancel',
                    confirm: 'Delete',
                    success_disabled: '%{count} client(s) disabled',
                    success_permanent: '%{count} permanently deleted',
                    error: 'Error deleting clients',
                },
                applications: {
                    title: 'Application Links',
                    title_openid: 'OpenID Application Settings',
                    no_links: 'No application links',
                    fields: {
                        application: 'Application',
                        api_key_override: 'API Key Override',
                        redirect_uris: 'Redirect URIs',
                        login_uri: 'Login URI',
                        authorize_uris: 'Authorize URIs',
                        scope: 'Scope',
                        response_type: 'Response Type',
                        access_token_name: 'Access Token Name',
                        use_refresh_token: 'Use Refresh Token',
                        status: 'Status',
                        actions: 'Actions',
                    },
                    dialog: {
                        add_title: 'Add Application Link',
                        edit_title: 'Edit Application Link',
                        application_guid: 'Application GUID',
                        redirect_uris_helper: 'One URI per line',
                        uris_helper: 'One URI per line',
                        comma_separated_helper: 'Comma separated',
                        cancel: 'Cancel',
                        save: 'Save',
                        add: 'Add',
                    },
                    confirm_delete: 'Remove this application link?',
                    notify: {
                        removed: 'Application link removed',
                        updated: 'Application link updated',
                        added: 'Application link added',
                    },
                },
                audit: 'Audit',
            },
            error: 'Error loading data',
            ecs: {
                title: 'ECS Services',
                no_services: 'No services found',
                fields: {
                    serviceName: 'Service',
                    status: 'Status',
                    running: 'Running',
                    desired: 'Desired',
                    pending: 'Pending',
                    taskDefinition: 'Task Definition',
                    lastEvent: 'Last Event',
                },
            },
            rds: {
                title: 'RDS/Aurora Clusters',
                no_clusters: 'No clusters found',
                fields: {
                    cluster: 'Cluster',
                    status: 'Status',
                    engine: 'Engine',
                    endpoint: 'Endpoint (writer)',
                    readerEndpoint: 'Endpoint (reader)',
                    instances: 'Instances',
                },
            },
            rds_metrics: {
                title: 'RDS Performance',
                no_cluster: 'No clusters available',
                charts: {
                    cpu: 'CPU Utilization (%)',
                    connections: 'Database Connections',
                    readLatency: 'Read Latency',
                    writeLatency: 'Write Latency',
                    readIOPS: 'Read IOPS',
                    writeIOPS: 'Write IOPS',
                    freeableMemory: 'Freeable Memory',
                    networkReceive: 'Network Receive',
                    networkTransmit: 'Network Transmit',
                },
            },
        },
        pos: {
            search: 'Search',
            configuration: 'Settings',
            language: 'Language',
            theme: {
                name: 'Theme',
                light: 'Light',
                dark: 'Dark',
            },
            filter: 'Filtered by',
            dashboard: {
                welcome: {
                    title: 'Welcome to the new support interface.',
                    subtitle: 'This is the new support interface.',
                },
            },
        },
        resources: {
            user: {
                name: 'Administrator |||| Administrators |||| All administrators',
                fields: {
                    // group: 'Group',
                    // groups: 'Groups',
                    last_login: 'Last login',
                    registered: 'Registered',
                    name: 'Name',
                    username: 'Username',
                    status: 'Status',
                    password: 'Password',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        login_not_allowed: 'Login not allowed',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Profile locked',
                        profile_locked_message: 'Send unlock message',
                        profile_locked_description:
                            'If the unlock message is not sent, the user can request the unlock message themselves when trying to log in to the service.',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    group: {
                        name: 'Name',
                        valid: 'Validity period',
                        valid_desc:
                            'Leave the fields empty if you do not want to set a validity period.',
                        ofduty: 'On leave',
                        isadmin: 'Administrator',
                        role: 'Role',
                        subrole: 'Teaching subjects',
                        status: 'Status',
                    },
                    applicationToAdd: 'Service to add',
                    application: {
                        name: 'Service',
                        isadmin: 'Administrator',
                        role: 'Role',
                        status: 'Status',
                    },
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    schools: 'Schools',
                    'schools.search': 'Search schools',
                    schools_close: 'Schools',
                },
                tabs: {
                    profile: 'Profile',
                    application: 'Services',
                    logs: 'Event log',
                },
                status: {
                    0: 'Active',
                    1: 'Removed',
                    2: 'Pending confirmation',
                    3: 'Unregistered',
                    10: 'Possible duplicate',
                },
                invitation: {
                    badge: 'Invitation',
                    title: 'Manage invitation',
                    pending: 'Invitation pending',
                    none: 'No pending invitations',
                    dialog: {
                        title: 'Manage user invitation',
                        content:
                            'Send invitation to user %{name} (%{username}) to this school',
                        confirm: 'Send invitation',
                        cancel: 'Cancel',
                        language: 'Language',
                        sent_count:
                            'Invitation has been sent %{count} times before',
                        not_sent: 'Invitation has not been sent yet',
                    },
                },
                session: {
                    remaining: 'Session remaining',
                    active_sessions: 'active sessions',
                    longest: 'longest',
                    confirm_remove:
                        'Are you sure you want to remove the session and log the user out?',
                    confirm_remove_all:
                        'Are you sure you want to remove all %{count} sessions and log the user out from all devices?',
                },
                help: {
                    modifyuserapp: 'Modify user service settings.',
                },
                actions: {
                    approve: 'Approve',
                    changepassword: 'Change password',
                    reapprove: 'Reapprove',
                    remove_mpass: 'Remove MPASS link',
                    remove_session: 'Remove session and log user out.',
                    new: {
                        title: 'New user',
                    },
                    reset_password: {
                        title: 'Password reset link',
                        help_text:
                            'The password reset link is one-time use and expires after use. Note: Requesting a new reset link always invalidates all previously created, unused links.',
                        button_label: 'Password reset link',
                        description: `Hello!

Here is the link to reset your password: 
%{link}. 
Copy the link and open it in your browser. You will then be able to create a new password.

Best regards
Otava Learning
`,
                    },
                },
            },
            teacher: {
                name: 'Teacher |||| Teachers',
                actions: {
                    back: 'Back to training information',
                    approve: {
                        button_title: 'Approve',
                        dialog: {
                            title: 'Approve %{mode}',
                            content:
                                'You are approving user %{name} (%{username})',
                            schools:
                                'Schools to which the user will be attached',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    remove: {
                        button_title: 'Remove',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming removal of user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Remove user from groups of the schools being removed',
                            remove_user: 'Mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    unremove: {
                        button_title: 'Restore',
                        success: 'Action succeeded',
                        error: 'Restore failed',
                    },
                    new: {
                        title: 'New teacher',
                        sendmail: 'Send message to teacher',
                        lang: 'Language',
                        application: 'Service',
                        messagepreview: 'Message preview',
                        nomailtemplate:
                            'The service does not have a function implemented. The message cannot be sent.',
                        mailtemplateinactive:
                            'The message template is not active. The message cannot be sent.',
                    },
                    changepassword: 'Change password',
                    reapprove: 'New approval request',
                },
                fields: {
                    last_login: 'Last login',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Work email address',
                    status: 'Status',
                    password: 'Password',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        learning_environment_1: 'Google Classroom',
                        learning_environment_2: 'Office 365',
                        learning_environment_3: 'Kampus',
                        learning_environment_4: 'Moodle',
                        learning_environment_5: 'Itslearning',
                        learning_environment_6: 'Peda.net',
                        learning_environment_free: 'Some other, what?',
                        login_not_allowed: 'Login not allowed',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Profile locked',
                        'infra:merged': 'Merged',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    schools: 'Schools',
                    groups: 'Groups',
                    options: 'Options',
                    school: {
                        title: 'School',
                        registered: 'Registered',
                        settings: 'Settings',
                        group: 'Group',
                    },
                    group: {
                        valid: 'Validity period',
                        valid_desc:
                            'Leave the fields empty if you do not want to set a validity period.',
                        ofduty: 'On leave',
                        isadmin: 'Permissions to update school information',
                        role: 'Role',
                        subrole: 'Subjects',
                    },
                    role: {
                        guid: 'Role',
                        subjects: 'Subjects',
                    },
                    application: 'Service',
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic Information',
                    schools_and_groups: 'Schools and Groups',
                    materials: 'In Use',
                    merge: 'Merging',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'On Leave',
                    logs: 'Logs',
                    status: 'Status',
                    application: 'Services',
                },
                page: {
                    delete: 'Delete teacher',
                    create: 'Add teacher',
                    addSchool: 'Add school',
                    addApp: 'Add service',
                    changepassword: {
                        title: 'Change password',
                        oldpassword: 'Old password',
                        newpassword: 'Enter new password',
                        responses: {
                            401: 'Old password is incorrect',
                            404: 'User not found',
                            500: 'Password change failed, please try again later.',
                            200: 'Password has been changed',
                        },
                    },
                },
                status: {
                    0: 'Active |||| Active',
                    1: 'Deleted |||| Deleted',
                    2: 'Pending Confirmation |||| Pending Confirmation',
                    3: 'Unregistered |||| Unregistered',
                },
                help: {
                    createform:
                        'Create a new teacher and link them to a school. The account is valid indefinitely unless a validity period is specified.',
                    modifyschoolform: "Edit the teacher's school information.",
                },
                empty_header: 'No teachers',
            },
            student: {
                name: 'Student |||| Students',
                actions: {
                    back: 'Back to school information',
                    changepassword: 'Change password',
                    reapprove: 'Resend confirmation request',
                    gdpr: {
                        button_title: 'GDPR Removal',
                        success: 'GDPR removal succeeded',
                        error: 'GDPR removal failed',
                        dialog: {
                            title: 'GDPR Removal',
                            content:
                                'You are deleting student %{name} (%{email}). This action cannot be undone.',
                            describe_service:
                                'Deletion request for %{service} with ID: %{id}',
                            extra_content:
                                'NOTE! Save GDPR deletion requests before deleting.',
                            confirm: 'Delete',
                            cancel: 'Cancel',
                        },
                    },
                    new: {
                        title: 'New student',
                    },
                    export: {
                        10: 'Export duplicates to Excel',
                    },
                    approve: {
                        button_title: 'Approve',
                        dialog: {
                            title: 'Approve %{mode}',
                            content:
                                'You are approving user %{name} (%{username})',
                            schools: 'Schools the user will be linked to',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Approve',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Approval failed',
                    },
                    remove: {
                        button_title: 'Remove',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming the removal of user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Also remove user from groups in the removed schools',
                            remove_user: 'Also mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action succeeded',
                        error: 'Confirmation failed',
                    },
                },
                fields: {
                    last_login: 'Last login',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Textbook service username',
                    status: 'Status',
                    password: 'Password',
                    email: 'Email address',
                    alt_email: 'Alternative email addresses',
                    materialcharge: {
                        title: 'Material fee status',
                        no: 'No',
                        yes: 'Yes',
                        unknown: 'Unknown',
                    },
                    school: {
                        title: 'School',
                        registered: 'Joined',
                    },
                    public: {
                        email: 'Email address',
                        emails: 'Alternative email addresses',
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        description: 'Description',
                        login_not_allowed: 'Disallow login',
                        validfrom: 'Valid from',
                        validto: 'Valid to',
                        profile_locked: 'Account locked',
                        'infra:merged': 'Merged',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    group: {
                        valid: 'Account validity period',
                        valid_desc:
                            'Leave fields empty if you do not want to set a validity period.',
                        validfrom: 'Start date',
                        validto: 'End date',
                        ofduty: 'On leave',
                        isadmin: 'Permission to update school info',
                        role: 'Role',
                        subrole: 'Subjects',
                    },
                    application: 'Service',
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic information',
                    schools_and_groups: 'Schools and Groups',
                    materials: 'In Use',
                    merge: 'Merging',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'On Leave',
                    logs: 'Logs',
                    status: 'Status',
                    application: 'Services',
                },
                page: {
                    delete: 'Delete teacher',
                    create: 'Add teacher',
                    addSchool: 'Add school',
                    addApp: 'Add service',
                    changepassword: {
                        title: 'Change password',
                        oldpassword: 'Old password',
                        newpassword: 'Enter new password',
                        responses: {
                            401: 'Old password incorrect',
                            404: 'Invalid user',
                            500: 'Password change failed, please try again later.',
                            200: 'Password has been changed',
                        },
                    },
                },
                status: {
                    0: 'Active |||| Active',
                    1: 'Deleted |||| Deleted',
                    2: 'Pending confirmation |||| Pending confirmation',
                    3: 'Unregistered |||| Unregistered',
                },
                help: {
                    createform:
                        'Create a new teacher and link them to a school. The account is valid indefinitely unless a validity period is set.',
                    modifyschoolform: 'Edit teacher’s school information.',
                },
                empty_header: 'No students',
            },
            pupil: {
                name: 'Pupil |||| Pupils',
                codes: [
                    'aalto',
                    'aamu',
                    'aarre',
                    'ahven',
                    'aine',
                    'aisti',
                    'aito',
                    'akseli',
                    'alasin',
                    'alku',
                    'alpakka',
                    'askel',
                    'atlantti',
                    'atomi',
                    'aurinko',
                    'avain',
                    'elo',
                    'hanko',
                    'happi',
                    'harakka',
                    'harju',
                    'hattu',
                    'hauki',
                    'haukka',
                    'heitto',
                    'helmi',
                    'herhiläinen',
                    'hiekka',
                    'hiili',
                    'hiiri',
                    'hiutale',
                    'hopea',
                    'huhta',
                    'huhti',
                    'huippu',
                    'hukka',
                    'hylly',
                    'hymy',
                    'höyhen',
                    'ikkuna',
                    'ilo',
                    'ilta',
                    'ilves',
                    'into',
                    'jalava',
                    'jalo',
                    'joki',
                    'jono',
                    'jousi',
                    'juhla',
                    'juuri',
                    'jälki',
                    'jälki',
                    'kaarre',
                    'kaiku',
                    'kakku',
                    'kala',
                    'kallio',
                    'kameli',
                    'kani',
                    'kaniini',
                    'kansi',
                    'karhu',
                    'kartta',
                    'karviainen',
                    'kasse',
                    'kasvi',
                    'kaura',
                    'kauris',
                    'keino',
                    'keinu',
                    'kello',
                    'keltainen',
                    'kerho',
                    'kerros',
                    'kesä',
                    'kettu',
                    'kevät',
                    'kielo',
                    'kierros',
                    'kierto',
                    'kimalainen',
                    'kipinä',
                    'kirahvi',
                    'kirja',
                    'kirjain',
                    'kirjasto',
                    'kirsu',
                    'kissa',
                    'kivi',
                    'koira',
                    'koivu',
                    'koju',
                    'kontio',
                    'korento',
                    'kori',
                    'koti',
                    'koulu',
                    'kuikka',
                    'kuitu',
                    'kukka',
                    'kulku',
                    'kulma',
                    'kulta',
                    'kuohu',
                    'kupari',
                    'kurki',
                    'kurre',
                    'kuu',
                    'kuusi',
                    'kuva',
                    'kyyhky',
                    'käpy',
                    'käpälä',
                    'lahja',
                    'lahti',
                    'laine',
                    'laituri',
                    'laiva',
                    'lammas',
                    'lasi',
                    'lasku',
                    'latva',
                    'laulu',
                    'laulu',
                    'lehti',
                    'lehto',
                    'leijona',
                    'leikki',
                    'leipä',
                    'lento',
                    'leopardi',
                    'leppä',
                    'liekki',
                    'liina',
                    'lintu',
                    'lippu',
                    'lohi',
                    'loiste',
                    'luku',
                    'lumi',
                    'lumo',
                    'luokka',
                    'luonto',
                    'maa',
                    'maailma',
                    'maasto',
                    'maila',
                    'majakka',
                    'manner',
                    'mansikka',
                    'marmori',
                    'matka',
                    'mehiläinen',
                    'meri',
                    'metsä',
                    'miekkavalas',
                    'mustikka',
                    'muurahainen',
                    'myrsky',
                    'mäki',
                    'nappi',
                    'nauha',
                    'neptunus',
                    'niemi',
                    'niitty',
                    'numero',
                    'nummi',
                    'nuotio',
                    'oiva',
                    'oksa',
                    'olki',
                    'omena',
                    'onki',
                    'onni',
                    'opas',
                    'oppi',
                    'orava',
                    'otso',
                    'ovi',
                    'paiste',
                    'pala',
                    'pallo',
                    'paperi',
                    'pelikaani',
                    'perho',
                    'peura',
                    'pihlaja',
                    'pino',
                    'pinta',
                    'pisara',
                    'pluto',
                    'pohjanmeri',
                    'polku',
                    'portti',
                    'pronssi',
                    'puhe',
                    'puhuri',
                    'punainen',
                    'puomi',
                    'purje',
                    'puro',
                    'puu',
                    'pyy',
                    'päivä',
                    'raikas',
                    'raita',
                    'raita',
                    'ranta',
                    'rastas',
                    'rata',
                    'ratas',
                    'rauta',
                    'reitti',
                    'repo',
                    'reppu',
                    'retki',
                    'riemu',
                    'ruis',
                    'rusko',
                    'ruusu',
                    'sade',
                    'sakset',
                    'salmi',
                    'sammal',
                    'sarja',
                    'sato',
                    'sauna',
                    'savu',
                    'siipi',
                    'silta',
                    'simpukka',
                    'sininen',
                    'sisko',
                    'sivu',
                    'sointu',
                    'soitin',
                    'solki',
                    'sudenkorento',
                    'sulka',
                    'sumu',
                    'syksy',
                    'syksy',
                    'syys',
                    'sävel',
                    'taika',
                    'taimen',
                    'taimi',
                    'taito',
                    'taituri',
                    'talo',
                    'talvi',
                    'tammi',
                    'tanner',
                    'tarha',
                    'tasku',
                    'tassu',
                    'telkkä',
                    'tellus',
                    'tenho',
                    'teos',
                    'tiainen',
                    'tie',
                    'tieto',
                    'tiikeri',
                    'tiira',
                    'tikka',
                    'tikkari',
                    'tikli',
                    'tilke',
                    'timantti',
                    'tina',
                    'torni',
                    'touko',
                    'tovi',
                    'tuli',
                    'tunti',
                    'tunturi',
                    'tuohi',
                    'tuokio',
                    'tuoli',
                    'tuomi',
                    'tuore',
                    'tupa',
                    'tuuli',
                    'tyvi',
                    'tähti',
                    'täky',
                    'uikku',
                    'ukko',
                    'ulappa',
                    'usva',
                    'vaaka',
                    'vadelma',
                    'vahti',
                    'valkoinen',
                    'valo',
                    'varpunen',
                    'vasta',
                    'vasu',
                    'vene',
                    'vene',
                    'veto',
                    'vety',
                    'vihreä',
                    'vihta',
                    'viima',
                    'viiri',
                    'vimma',
                    'violetti',
                    'virta',
                    'voima',
                    'vuori',
                    'vuoro',
                    'välke',
                ],
                actions: {
                    new: {
                        title: 'New pupil',
                    },
                    export: {
                        '10': 'Export duplicates to Excel',
                    },
                    approve: {
                        button_title: 'Confirm',
                        dialog: {
                            title: 'Confirm %{mode}',
                            content:
                                'You are confirming the user %{name} (%{username})',
                            schools: 'Schools the user will be associated with',
                            sendmail: 'Send confirmation email',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action successful',
                        error: 'Confirmation failed',
                    },
                    remove: {
                        button_title: 'Delete',
                        dialog: {
                            title: 'Confirm %{mode} removal',
                            content:
                                'You are confirming the user %{name} (%{username})',
                            schools:
                                'Schools from which the user will be removed',
                            remove_groups:
                                'Also remove user from groups in the removed schools',
                            remove_user: 'Also mark user (%{name}) as removed',
                            cancel: 'Cancel',
                            confirm: 'Confirm',
                            language: 'Language',
                        },
                        success: 'Action successful',
                        error: 'Confirmation failed',
                    },
                },
                fields: {
                    last_login: 'Logged in',
                    registered: 'Registered',
                    joined: 'Joined',
                    modified: 'Modified',
                    name: 'Name',
                    username: 'Email',
                    license_status: 'License status',
                    licenses: 'In use',
                    password: 'Password',
                    public: {
                        firstname: 'First name',
                        middlename: 'Middle name',
                        lastname: 'Last name',
                        code: 'Key code',
                        login_not_allowed: 'Disable login',
                        validfrom: 'Starts',
                        validto: 'Ends',
                        profile_locked: 'Account locked',
                    },
                    school: {
                        title: 'School',
                        registered: 'Joined',
                    },
                    schools: 'Schools',
                    groups: 'Groups',
                    class: 'Class',
                    group_name: 'Class',
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    services: 'Sources',
                },
                tabs: {
                    profile: 'Basic info',
                    schools_and_groups: 'Schools and groups',
                    materials: 'In use',
                    merge: 'Merge',
                    schools: 'Schools',
                    classes: 'Classes',
                    role: 'Role',
                    subject: 'Subjects',
                    isadmin: 'Administrator',
                    ofduty: 'Off duty',
                    logs: 'Log',
                    status: 'Status',
                    application: 'Services',
                },
                page: {
                    add_pupils_title: 'Add pupils to class',
                    delete: 'Delete pupil',
                    create: 'Add pupil',
                    fromfile: 'Add from CSV file',
                },
                status: {
                    '0': 'Active',
                    '1': 'Deleted',
                    '2': 'Registration incomplete',
                    '3': 'Unregistered',
                },
                help: {
                    never_used: 'Never logged in',
                    createform:
                        'A key code is created for the pupil during the addition.',
                    createnewkey: 'Create a new key code for the pupil',
                },
                empty_header: 'No pupils',
            },
            events: {
                name: 'Event |||| Events',
            },
            transfer: {
                name: 'Transfer/Merging |||| Transfer/Mergings',
                fields: {
                    name: 'Icon',
                    item_type: 'Type code',
                    item_type_name: 'Type',
                    description_field1: 'Description 1',
                    description_field2: 'Description 2',
                    description_field3: 'Description 3',
                },
                filters: {
                    category: 'Series',
                    status: 'Status',
                    type: 'Type',
                    languages: 'Languages',
                    target_persons: 'Target group',
                    kouluasteet: 'School level',
                    applications: 'Applications',
                },
            },
            subproduct: {
                empty_header: 'No sub-products',
                empty_message: 'Add sub-products using the form below.',
            },
            product: {
                name: 'Product |||| Products',
                fields: {
                    id: 'Order link',
                    name: 'Name',
                    'public.alternative_name': 'Alternative name',
                    'private.tuoterekisteri_data.tuotenimi':
                        'Name in product register',
                    'public.category': 'Book series',
                    'public.languages': 'Languages',
                    'public.target_persons': 'Target group',
                    'public.show_testusage_button': 'Show trial button',
                    'public.kouluasteet': 'School level',
                    'public.applications': 'Applications where shown',
                    status: 'Status',
                    'public.product_type': 'Product type',
                    'public.modules': 'Modules',
                    'public.not_period_license':
                        'Not a seasonal license product',
                    is_group: 'Group',
                    'public.materialstorage.isbn': 'ISBN',
                    'public.materialstorage.alternative_isbn':
                        'ISBN (tolerant)',
                    path: 'Path',
                    filename: 'File',
                    show_only_in_list: 'Visibility',
                    show_only_in_list_input:
                        'Do not show as individual product',
                    show_only_to_subscribers: 'Show only to subscribers',
                    'public.dont_show_as_main_product':
                        'Show as main product only to subscribers',
                    'public.dont_show_as_sub_product':
                        'Do not show as sub-product in Nova',
                    free_to_download: 'Product is free',
                    'public.default_license_type': 'License type',
                    'public.default_license_period': 'Default license duration',
                    'public.disable_extra_material':
                        'Do not allow activation with ISBN',
                    'public.is_saamelaiskaraja': 'Sámi Parliament product',
                    'public.materialstorage.default_trial_period':
                        'Default trial period',
                    'public.materialstorage.mime_type': 'File type',
                    'public.materialstorage.path': 'Path',
                    materialstorage_guid: 'Material storage',
                    description: 'Intro/Marketing text',
                    'private.tuoterekisteri_data.saatavuus': 'Availability',
                    'private.tuoterekisteri_data.tl_tuote_esittelyteksti_info':
                        'Intro/Marketing text',
                    'private.tuoterekisteri_data.kantanumero': 'Root number',
                    'public.subject': 'Subject',
                    'public.additional_subjects': 'Other subjects',
                    'public.imprint': 'Publisher',
                    'public.course': 'Course',
                    'public.element': 'Element',
                    'public.class_level': 'Grade level',
                    'public.productgroup': 'Product group',
                    'public.is_for_extra': 'Part of high school user benefits',
                    'public.is_package': 'Package product',
                    is_for_extra_first:
                        'Part of first year high school user benefits',
                    'public.show_high_school_groups':
                        'Part of high school group selection',
                    'public.release_date': 'Additional info',
                    'public.free_to_download': 'Free',
                    'public.free_for_extra': 'For existing users',
                    order: 'Sort',
                    'public.sumplattava': 'Product is mixable',
                    'public.analytics': 'Product has analytics',
                    saatavuus: 'Availability',
                    modules: 'Modules',
                    'public.extendable': 'License is extendable',
                    'public.extendable_days': 'Extension time in days',
                    'user.firstname': 'First name',
                    'user.lastname': 'Last name',
                    'user.email': 'Email',
                    'license.valid_from': 'From',
                    'license.valid_until': 'Until',
                    'subscriber.name': 'Subscriber',
                    'subscription.name': 'Shared / Subscribed',
                    preview: 'Preview',
                },
                filters: {
                    category: 'Series',
                    category_close: 'Series',
                    status: 'Status',
                    status_close: 'Status',
                    type: 'Type',
                    type_close: 'Type',
                    languages: 'Language',
                    languages_close: 'Language',
                    target_persons: 'Target group',
                    target_persons_close: 'Target group',
                    kouluasteet: 'School level',
                    kouluasteet_close: 'School level',
                    applications: 'Applications',
                    applications_close: 'Applications',
                    materialstorage: 'Material storage',
                    materialstorage_close: 'Material storage',
                    kvstatus: 'Availability',
                    kvstatus_close: 'Availability',
                },
                tabs: {
                    details: 'Basic info',
                    extra_details: 'Additional info',
                    redirect_details: 'Redirect info',
                    tuotelista_details: 'Product register info',
                    history_details: 'History',
                    sub_details: 'Linked products',
                    modify_history: 'Change history',
                    usage_history: 'Usage history',
                    licenses: 'Licenses',
                },
            },
            subscription: {
                name: 'Order |||| Orders',
                fields: {
                    name: 'Name',
                    subscriber: 'Subscriber',
                    product: 'Product',
                    product_guid: 'Product',
                    subscriber_guid: 'Subscriber',
                    valid_from: 'From',
                    valid_until: 'Until',
                    valid_season: 'Valid',
                    license_count: 'Purchased',
                    used_license_count: 'Used',
                    used_license_teacher_count: 'In use',
                    unused_license_count: 'Available',
                    type: 'License type',
                    product_code: 'Product code',
                    activate_code: 'Activation code',
                    message: 'Reason for change',
                    license_user_name: 'User',
                    license_created_date: 'Activation',
                    license_modified_date: 'Modified',
                    status: 'Status',
                    srcond: 'Targeting',
                    created_start: 'Created (from)',
                    created_end: 'Created (to)',
                    modified_start: 'Modified (from)',
                    modified_end: 'Modified (to)',
                    created: 'Created',
                    modified: 'Modified',
                    'product.name': 'Product',
                    'subscriber.name': 'Subscriber',
                    'public.kausi': 'Season',
                    'public.kvean': 'EAN',
                    'public.kvnro': 'Order number(s)',
                    'public.kvsource': 'Source',
                    'public.kvetumaara': 'Front amount',
                    'public.kvkoulukoodi': 'School code',
                    'public.kvtilausmaara': 'Order quantity',
                    combination_of_licenses: 'Til./Etu/Muu',
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    products: 'Product',
                    'products.search': 'Enter search terms',
                    products_close: 'Product',
                    subscribers: 'Subscriber',
                    'subscribers.search': 'Enter search terms',
                    subscribers_close: 'Subscriber',
                    isvalid: 'Validity',
                    isvalid_close: 'Validity',
                    activationcode: 'Activation code',
                    activationcode_search: 'Enter activation code',
                    activationcode_found:
                        'Activation code has been used and subscription found',
                    activationcode_not_found:
                        'No subscription found for this activation code in Storia',
                    activationcode_not_found_title:
                        'Activation code - not found',
                    activationcode_not_activated:
                        'Product has not been activated yet',
                    activationcode_not_activated_title:
                        'Activation code - not activated',
                    activationcode_activated_title:
                        'Activation code - activated',
                    activationcode_activated_at: 'Activated',
                    activationcode_invalid:
                        'Invalid activation code format (e.g. YWA4-32HF-YFVJ-9EK4-B4R7)',
                },
                tabs: {
                    details: 'Basic info',
                    license_details: 'In use',
                    messages: 'Notes',
                    public_details: 'Guidance info',
                    private_details: 'Product register info',
                    log: 'Event log',
                    modify_history: 'Change history',
                    usage_history: 'Usage history',
                    groups: 'Groups',
                    kv: 'Book distribution',
                },
                page: {
                    add_license: 'Share license',
                },
                action: {
                    accept: 'Add one',
                    reject: 'Remove one',
                },
                actions: {
                    remove: {
                        title: 'Obs !',
                    },
                },
                notification: {
                    approved_success: 'Addition successful',
                    approved_error: 'Error: Addition failed',
                    rejected_success: 'Removal successful',
                    rejected_error: 'Error: Removal failed',
                },
                license_status: {
                    0: 'In use',
                    1: 'Removed',
                    2: 'Granted',
                },
                license_action: {
                    delete: 'Delete',
                    unuse: 'Release',
                },
                status: {
                    0: 'Public',
                    1: 'Removed',
                },
                srcond: {
                    1: 'Unattributed',
                    2: 'Attributed',
                },
            },
            userlicense: {
                name: 'License |||| Licenses',
                fields: {
                    lastname: 'Last name',
                    firstname: 'First name',
                    email: 'Email',
                    product: 'Product name',
                    product_isbn: 'ISBN',
                    licenses: 'Licenses',
                    groups: 'Select group',
                },
            },
            userlist: {
                name: 'User |||| Users',
                fields: {
                    lastname: 'Last name',
                    firstname: 'First name',
                    middlename: 'Middle name',
                    username: 'Username',
                    email: 'Email',
                    role: 'Role',
                    subjects: 'Subjects',
                    code: 'Key code',
                },
            },
            school: {
                label: 'School',
                name: 'School |||| Schools',
                fields: {
                    empty: '(Not provided)',
                    empty_web_code:
                        'Web shop code not found, contact Otava customer service',
                    category_id: 'Category',
                    name: 'Name',
                    keyfields: 'Key details',
                    keyfields_more:
                        'Key details (school code, type, and location)',
                    material: 'Material',
                    public: {
                        hidepages: 'Hide pages from users',
                        categories: 'Series',
                        features: 'Debug features',
                        address: 'Mailing address',
                        city: 'City',
                        code: 'School code',
                        name: 'Name',
                        type: 'School type',
                        school_email: 'School official email rule',
                        phone_1: 'Teachers’ lounge phone 1',
                        phone_2: 'Teachers’ lounge phone 2',
                        location: 'Location',
                        zip_code: 'Postal code',
                        grade_max: 'Highest grade level',
                        grade_min: 'Lowest grade level',
                        headmaster: 'Principal',
                        identifier: 'Identifier',
                        shop_password: 'Password',
                        visit_city: 'Visit city',
                        last_checked: 'Last checked',
                        office_phone: 'Office phone',
                        visit_address: 'Visit address',
                        visit_zip_code: 'Visit postal code',
                        headmaster_email: 'Principal email',
                        headmaster_phone: 'Principal phone',
                        headmaster_cellphone: 'Principal mobile',
                        number_of_pupils: 'Number of pupils',
                        number_of_teachers: 'Number of teachers',
                        grades: 'Grade levels',
                        web_code: 'Web shop code',
                    },
                    'private.services.crm.crmGUID': 'CRM link',
                },
                filters: {
                    types: 'School types',
                    types_close: 'School types',
                    type_groups: 'School type groups',
                    type_groups_close: 'School type groups',
                    status: 'Status',
                    status_close: 'Status',
                    adminstatus: 'Administrators',
                    adminstatus_close: 'Administrators',
                    teacherstatus: 'Teachers',
                    teacherstatus_close: 'Teachers',
                    pupilstatus: 'Pupils',
                    pupilstatus_close: 'Pupils',
                    studentstatus: 'Students',
                    studentstatus_close: 'Students',
                    subscriptionstatus: 'Subscriptions',
                    subscriptionstatus_close: 'Subscriptions',
                },
                actions: {
                    show_web_shop_code: 'Show web shop code',
                    remove: {
                        title: 'Note!',
                        ask: 'Remove user from school %{name}',
                        description:
                            'Do you want to remove the user from school %{name}?',
                    },
                    add: {
                        title: 'Note!',
                        ask: 'Restore user to school %{name}',
                        description:
                            'Do you want to restore the user to school %{name}?',
                    },
                },
                page: {
                    manage_license: 'Pupil licenses',
                    pupil_license: {
                        addall: 'Select all',
                        removeall: 'Remove selections',
                        chosen: 'Selected',
                    },
                    student_license: {
                        code: 'Activation code',
                        name: 'Name',
                        aktivated: 'Activated',
                    },
                    web_code: 'Web shop code is used in Otava’s web store.',
                    web_code_username: 'Username: %{username}',
                    web_code_password: 'Password: %{password}',
                    orders: {
                        pupil: 'School’s pupil licenses',
                        school: 'School’s educational materials and digital book licenses',
                        past: 'Expired',
                        active: 'Active',
                        future: 'Upcoming',
                    },
                },
                help: {
                    manage_license: {
                        product: 'Material',
                        hint: 'Select material',
                        beforeProductSelected:
                            'Start assigning pupil licenses by selecting a material from the dropdown menu below.',
                        afterProductSelected:
                            'Select pupils from the list to assign licenses to. Don’t forget to save your changes by clicking the Save button. Pupils are listed by class. You can also add new pupils to classes by clicking the Add pupil button for each class.',
                        afterProductSelected2:
                            'You can also remove a pupil license from a pupil who has not yet used the material.',
                    },
                },
                tabs: {
                    details: 'Basic information',
                    subscription: 'Subscriptions',
                    groups: 'Groups',
                    teachers: 'Teachers',
                    pupils: 'Pupils',
                    students: 'Students',
                    stats: 'Statistics',
                },
                organization: {
                    not_found:
                        'Organization information not found from Opintopolku service. Please check the school code.',
                    error: 'Error fetching organization information',
                    inactive: 'Organization is not active',
                },
            },
            group: {
                name: 'Group |||| Groups',
                fields: {
                    name: 'Name',
                    status: 'Status',
                    parent_group: 'School',
                    schools_name: 'Schools',
                    'public.code': 'Join code',
                    'public.group_type': 'Group type',
                    'public.archived': 'Archived',
                    settings: {
                        allowOnlySameSchool: 'Allow Only Same School',
                        allowOnlyMPASS: 'Allow Only MPASS',
                    },
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                    archived: 'Archived',
                },
                tabs: {
                    details: 'Basic information',
                    teachers: 'Teachers',
                    pupils: 'Pupils',
                    students: 'Students',
                    settings: 'Settings',
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    archived: 'Archive',
                    archived_close: 'Archive',
                    schools: 'Schools',
                    schools_close: 'Schools',
                    'schools.search': 'Search school',
                },
            },
            usergroup: {
                empty_header: 'No groups',
                filters: {
                    onlyactive: 'Only active groups',
                },
                status: {
                    0: 'Active',
                    1: 'Removed',
                },
            },
            announcement: {
                name: 'Announcement |||| Announcements',
                fields: {
                    name: 'Name',
                    'public.message': 'Text',
                    'public.read_more': 'Read more link (format: https://)',
                    valid_from: 'From',
                    valid_until: 'Until',
                    valid_season: 'Valid period',
                    status: 'Status',
                    'public.applications': 'Services',
                    placements: 'Placement',
                    'public.languages': 'Language',
                    'public.categories': 'Series',
                    'public.types': 'School types',
                    'public.tags':
                        'ISBN targeting (comma-separated list of ISBNs)',
                    'public.typegroups': 'School type groups',
                    timestamp: 'Modified',
                    'public.announcement_type': 'Announcement type',
                    created: 'Created',
                    modified: 'Modified',
                    creator_name: 'Creator',
                    modifier_name: 'Modifier',
                    author: {
                        creator: 'Creator',
                        created: 'Created',
                        modifier: 'Modifier',
                        modified: 'Modified',
                    },
                },
                filters: {
                    status: 'Status',
                    status_close: 'Status',
                    languages: 'Language',
                    languages_close: 'Language',
                    applications: 'Applications',
                    applications_close: 'Applications',
                    announcement_types: 'Announcement type',
                    announcement_types_close: 'Announcement type',
                    online_status: 'Online status',
                    online_status_close: 'Online status',
                },
                tabs: {
                    details: 'Basic information',
                },
                notification: {
                    approved_success: 'Addition successful',
                    approved_error: 'Error: Addition failed',
                    rejected_success: 'Deletion successful',
                    rejected_error: 'Error: Deletion failed',
                },
                status: {
                    0: 'Public',
                    1: 'Deleted',
                    2: 'Draft',
                },
                language: {
                    fin: 'Finnish',
                    swe: 'Swedish',
                    eng: 'English',
                },
                application: {
                    oppimisenpalvelut: 'oppimisenpalvelut.otava.fi',
                    opepalvelu: 'opepalvelu.otava.fi',
                    opepalvelubeta: 'opepalvelu-beta.otava.fi',
                    oppilas: 'oppilas.otava.fi',
                    opiskelija: 'opiskelija.otava.fi',
                    finnlectura: 'finnlectura',
                    finnlectura_opiskelija: 'opiskelija.finnlectura.fi',
                    finnlectura_opettaja: 'opettaja.finnlectura.fi',
                },
                placement: {
                    1: 'Top banner',
                    2: 'Lower top banner',
                    3: 'With series',
                    4: 'News with series',
                    5: 'Message box',
                },
            },
            application: {
                label: 'Palvelu',
                name: 'Palvelu |||| Palvelut',
                actions: {
                    remove: {
                        title: 'Huom !',
                        ask: 'Poista käyttäjä palvelusta %{name}',
                        description:
                            'Poistetaanko käyttäjä palvelusta %{name} ?',
                    },
                },
                tabs: {
                    details: 'Perustiedot',
                },
                fields: {
                    last_login: 'Kirjautunut',
                    registered: 'Rekisteröitynyt',
                    name: 'Nimi',
                    username: 'Sähköposti',
                    alternative_isbn_in_use: 'Korvaava ISBN käytössä',
                    alternative_exclude_materialstorage:
                        'Älä käytä näissä materiaalivarastoissa',
                    alternative_exclude: 'Älä käytä näillä ISBN:llä',
                    alternative_isbn_text_fi: 'Suomenkielinen teksti',
                    alternative_isbn_text_sv: 'Ruotsinkielinen teksti',
                },
                status: {
                    0: 'aktiivinen',
                    1: 'Poistettu',
                },
                role: {
                    400: 'Superadmin',
                    300: 'Pääkäyttäjä',
                    250: 'Tuotepäivittäjä',
                },
            },
            valkey: {
                name: 'Valkey |||| Valkey',
                fields: {
                    key: 'Key',
                    value: 'Value',
                    ttl: 'TTL (s)',
                    size: 'Size',
                    type: 'Storage type',
                },
                filters: {
                    pattern: 'Search keys (wildcard: *)',
                },
                actions: {
                    delete_confirm:
                        'Are you sure you want to delete key %{key}?',
                },
            },
            secrets: {
                name: 'Secrets Manager |||| Secrets Manager',
                fields: {
                    name: 'Name',
                    description: 'Description',
                    secretString: 'Value',
                    createdDate: 'Created',
                    lastChangedDate: 'Last Changed',
                    lastAccessedDate: 'Last Accessed',
                    deletionDate: 'Deletion Date',
                    tags: 'Tags',
                    versionId: 'Version',
                },
                helpers: {
                    name: 'Name must start with prefix infra2/',
                },
            },
            logevent: {
                s: {
                    filter: {
                        date_from: 'Mistä',
                        date_to: 'Mihin',
                    },
                    fields: {
                        date: 'Ajankohta',
                        author: 'Käyttäjä',
                        title: 'Lisenssimäärä',
                        subtitle: 'Lisenssimäärän muutos',
                        description: 'Viesti',
                    },
                },
                p: {
                    filter: {
                        date_from: 'Mistä',
                        date_to: 'Mihin',
                    },
                    fields: {
                        date: 'Ajankohta',
                        author: 'Käyttäjä',
                        title: '',
                        subtitle: 'Private',
                        description: 'Public',
                    },
                },
            },
            locks: {
                overlay: 'Currently Edited by %{name}',
            },
        },
        login: {
            otava_ad: 'Otava login',
            otava_ad_success: 'Continue to service',
            otava_ad_failure: 'Continue to login page',
            nonce_mismatch:
                'Your account is logged in on another browser. Please log in again.',
            no_token: 'Log in to continue',
        },
        statistics: {
            title: 'Statistics |||| Statistics',
            nodata: 'No data for the selected period',
            modes: {
                single: 'Reading',
                list: 'Table',
                graph: 'Chart',
            },
            fields: {
                teachers: 'Teachers',
                pupils: 'Pupils',
                students: 'Students',
                all: 'All',
                from: 'From',
                timestamp: 'Timestamp',
                level: 'Level',
                product_guid: 'Product',
                message: 'Event',
                subscription_guid: 'Subscription',
                school_guid: 'School',
                group_guid: 'Group',
                user_agent: 'User agent',
                tags: 'Tags',
                ip: 'IP',
                show_more: 'Show more',
            },
            periods: {
                day: 'Day',
                week: 'Week',
                month: 'Month',
                year: 'Year',
                startperiod: 'Start of period (1.8.)',
                days30: 'Last 30 days',
                days90: 'Last 90 days',
            },
            activesessions: {
                title: 'Active sessions',
                tooltip: 'User has an active session in the service',
            },
            activeusers: {
                title: 'Active users',
                tooltip: 'Logged in during the last %{day} days',
            },
            newusers: {
                title: 'New users',
                tooltip: 'Registered during the last %{day} days',
            },
            producttransitions: {
                title: 'Product transitions',
                tooltip: 'TOP %{limit} product transitions during %{day} days',
                tooltip_limit: 'TOP %{limit} product transitions',
                tooltip_single: 'Product transitions during %{day} days',
            },
            userevents: {
                title: 'User events',
                tooltip: 'TOP %{limit} events during %{day} days',
                tooltip_single: 'User events during %{day} days',
            },
        },
        merge: {
            title: 'Merging',
            nodata: 'No accounts to merge found',
            error: 'Merging failed',
            inactive: 'User is not active, account cannot be merged',
            success: 'Merging succeeded',
            button: 'Merge',
            cancel: 'Cancel',
            continue: 'Merge anyway',
            change_profile: 'Change to primary account',
            search: {
                title: 'Search for user to merge',
                description:
                    'Search for a user to merge with the primary account. The search term can be, for example, an email address or a name.',
                must_be_same_school: 'User must be from the same school.',
                button: 'Search',
            },
            from: {
                title: 'Account to merge',
            },
            to: {
                title: 'Primary account',
            },
            conflict: {
                title: 'Detected conflicts',
                message:
                    'Ensure that the accounts being merged belong to the same user. The following information is conflicting.',
                last_login: 'Last login',
                firstname: 'First name does not match',
                lastname: 'Last name does not match',
                mpass: 'Both have MPASS connection',
                school: 'School information does not match',
            },
            profilepage: {
                title: 'Linked accounts',
                description:
                    'The following accounts are linked to the user. You can remove a linked account by clicking the cross. NOTE! This action does not undo the merging.',
            },
        },
    }
);

export default customEnglishMessages;
